import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import type { UserDetailResponse } from '../types/api'

interface UserListProps {
  onEdit: (user: UserDetailResponse) => void
  onDelete: (userId: number) => void
  onManagePermissions: (user: UserDetailResponse) => void
  refreshTrigger: number
}

export default function UserList({ onEdit, onDelete, onManagePermissions, refreshTrigger }: UserListProps) {
  const [users, setUsers] = useState<UserDetailResponse[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)

  const fetchUsers = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getUsers({ limit, offset })
      setUsers(response.users)
      setTotal(response.total)
    } catch (err: any) {
      console.error('Failed to fetch users:', err)
      setError('ユーザー情報の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [limit, offset, refreshTrigger])

  const handleDeleteClick = (userId: number) => {
    if (window.confirm('このユーザーを削除してもよろしいですか？')) {
      onDelete(userId)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('ja-JP')
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">ユーザー一覧</h5>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-circle me-2" />
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">読み込み中...</span>
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2" />
            ユーザーがまだ登録されていません
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr className="table-light">
                    <th style={{ width: '15%' }}>ユーザー名</th>
                    <th style={{ width: '10%' }}>ロール</th>
                    <th style={{ width: '20%' }}>フルネーム</th>
                    <th style={{ width: '20%' }}>メール</th>
                    <th style={{ width: '15%' }}>作成日時</th>
                    <th style={{ width: '10%' }}>最終ログイン</th>
                    <th style={{ width: '10%' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <code>{user.username}</code>
                      </td>
                      <td>
                        {user.role === 'admin' ? (
                          <span className="badge bg-danger">管理者</span>
                        ) : (
                          <span className="badge bg-info">パワーユーザー</span>
                        )}
                      </td>
                      <td>{user.full_name || '-'}</td>
                      <td>{user.email || '-'}</td>
                      <td className="text-muted small">{formatDate(user.created_at)}</td>
                      <td className="text-muted small">
                        {user.last_login_at ? formatDate(user.last_login_at) : '-'}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => onEdit(user)}
                            title="編集"
                          >
                            <i className="fas fa-edit" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-info"
                            onClick={() => onManagePermissions(user)}
                            title="権限管理"
                          >
                            <i className="fas fa-lock" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteClick(user.id)}
                            title="削除"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <nav className="mt-4" aria-label="ページナビゲーション">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${offset === 0 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                      disabled={offset === 0}
                    >
                      <i className="fas fa-chevron-left me-1" />
                      前へ
                    </button>
                  </li>
                  <li className="page-item active">
                    <span className="page-link">
                      {Math.floor(offset / limit) + 1} / {Math.ceil(total / limit)}
                    </span>
                  </li>
                  <li className={`page-item ${offset + limit >= total ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setOffset(offset + limit)}
                      disabled={offset + limit >= total}
                    >
                      次へ
                      <i className="fas fa-chevron-right ms-1" />
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  )
}
